import { Color } from '@coast/schemas';
import { isNil } from 'lodash';
import React from 'react';
import { View } from '@shared/components/View';
import { Tag } from '@shared/components/blocks/lib/tags/tag';
import { MBBottomSheet, MBBottomSheetTitleBar } from '@shared/components/bottomSheet';
import { MBHover } from '@shared/components/hover';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBMultiSelect } from '@shared/components/multiSelect';
import { TagField } from '@shared/data/entity/fields/TagField';
import { TagComponent } from '@shared/scenes/workflows/config';
import { Colors, grid } from '@shared/styles';
import { useIsDesktop } from '@shared/util/hooks';
import { SingleComponentFormProps } from './SingleComponentForm';

interface Props extends SingleComponentFormProps {
  component: TagComponent;
}

export function TagComponentForm({
  fieldName,
  field,
  onUpdateField,
  anchorName,
  component,
  onClose,
  loading,
}: Props) {
  const tagField: TagField = field as TagField;
  const tagOptions = component.options ?? [];
  const activeOptions = tagOptions.filter(option => !option.archived || isNil(option.archived));
  const selectedOptions = tagOptions
    .filter(option => tagField?.tagIds.includes(option.value))
    .map(o => o.value);
  const max = component.multiselect ? 0 : 1;
  const isDesktop = useIsDesktop();

  const onChange = (newValues: string[]) => {
    const newField = new TagField(
      newValues,
      component.id,
      tagOptions,
      component.multiselect ?? false,
      component.addAdditionalValues ?? false,
    );
    onUpdateField(fieldName, newField);
    if (max === 1) {
      onClose();
    }
  };

  const match = (value: string, query: string) => {
    const label = tagOptions.find(o => o.value === value)?.label ?? '';
    return label.toLowerCase().includes(query.toLowerCase().trim());
  };

  return (
    <MBBottomSheet
      anchorName={anchorName}
      onClose={onClose}
      renderTitleBar={
        isDesktop
          ? undefined
          : () => (
              <MBBottomSheetTitleBar
                title={component.label ?? ''}
                rightIcon={'exit' as MBIconV2Name}
                rightIconColor={Colors.v2.purple}
                onRightIconPress={onClose}
              />
            )
      }
    >
      <View
        flex
        style={{
          width: isDesktop ? grid(44) : '100%',
          minHeight: isDesktop ? grid(44) : '0px',
          maxHeight: isDesktop ? grid(44) : '100%',
          justifyContent: loading ? 'center' : 'flex-start',
        }}
      >
        {loading && <MBLoadingIndicator />}
        {!loading && (
          <MBMultiSelect
            values={activeOptions.map(option => option.value)}
            selectedValues={selectedOptions}
            renderItem={item => {
              const option = tagOptions.find(tagOption => tagOption.value === item);
              return (
                <MBHover hoverStyle={{ backgroundColor: Colors.v2.backgroundGrayLight }}>
                  <View row centerVertically>
                    <View marginRight={1}>
                      <MBIconV2 name="tag" color={Colors.v2.purple} size={grid(3)} />
                    </View>
                    <Tag
                      color={(option?.backgroundColor as Color) ?? Color.tagGray}
                      label={option?.label ?? ''}
                    />
                  </View>
                </MBHover>
              );
            }}
            editMode={false}
            onChange={onChange}
            match={match}
            max={max}
            isRequired={component.required ?? undefined}
          />
        )}
      </View>
    </MBBottomSheet>
  );
}
